import ApiService, {
  publicHttpClient,
} from "@/core/services/api.service";
import { responseError } from "@/core/mixin/shared";
import querystring from "querystring";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });

export const MODULE_NAME = "anzeraChatbot";
export const BASE_URL = "api/ai/chat";

export const LOADING = "LOADING";
export const ERROR = "ERROR";
export const SUCCESS = "SUCCESS";

export const SET_LOADING = "M/LOADING";
export const SET_ERROR = "M/ERROR";
export const SET_SUCCESS = "M/SUCCESS";

export const AI_START_SESSION = "AI_START_SESSION";
export const AI_LOAD_HISTORY = "AI_LOAD_HISTORY";
export const AI_SEND_MESSAGE = "AI_SEND_MESSAGE";
export const AI_DOWNLOAD_REPORT = "AI_DOWNLOAD_REPORT";

export const STATUS = "STATUS";
export const SET_STATUS = "M/STATUS";

const state = {
  loading: false,
  error: null,
  success: null,
  status: "offline",
};

const getters = {
  [LOADING]: (state) => state.loading,
  [ERROR]: (state) => state.error,
  [SUCCESS]: (state) => state.success,
  [STATUS]: (state) => state.status,
};

const mutations = {
  [SET_LOADING](state, payload) {
    state.loading = !!payload;
  },
  [SET_ERROR](state, payload) {
    state.error = payload
      ? responseError(payload.response?.data || payload)
      : null;
  },
  [SET_SUCCESS](state, payload) {
    state.success = payload;
  },
  [SET_STATUS](state, payload) {
    state.status = payload === "online" ? "online" : "offline";
  },
};

function getCurrentLanguage() {
  try {
    return ls.get("language") || "tr";
  } catch {
    return "tr";
  }
}

function addLanguageParam(params = {}) {
  return {
    ...params,
    lang: getCurrentLanguage(),
  };
}

function addLanguageHeader(headers = {}) {
  return {
    ...headers,
    "X-Content-Language": getCurrentLanguage(),
  };
}

const actions = {
  [AI_START_SESSION]({ commit }) {
    commit(SET_LOADING, true);
    commit(SET_ERROR, null);
    commit(SET_SUCCESS, null);

    return ApiService.post(`${BASE_URL}/start`, {})
      .then((res) => {
        const be = (res?.data?.status || "").toLowerCase();
        commit(SET_STATUS, be === "active" ? "online" : "offline");
        commit(SET_SUCCESS, true);
        return res;
      })
      .catch((err) => {
        commit(SET_ERROR, err);
        commit(SET_STATUS, "offline");
        return Promise.reject(err);
      })
      .finally(() => commit(SET_LOADING, false));
  },

  [AI_LOAD_HISTORY]({ commit }, { sessionId, params = {} }) {
    const paramsWithLang = addLanguageParam(params);
    const qs = querystring.stringify(paramsWithLang);

    return ApiService.get(
      `${BASE_URL}/${sessionId}/messages${qs ? `?${qs}` : ""}`
    )
      .then((res) => {
        commit(SET_STATUS, "online");
        return res;
      })
      .catch((err) => {
        const code = err?.response?.status;
        if (!code || [401, 403, 404, 500].includes(code)) {
          commit(SET_STATUS, "offline");
        }
        return Promise.reject(err);
      });
  },

  [AI_SEND_MESSAGE]({ commit }, { sessionId, message }) {
    commit(SET_LOADING, true);
    commit(SET_ERROR, null);
    commit(SET_SUCCESS, null);

    const payload = {
      session_id: sessionId,
      message,
      lang: getCurrentLanguage(),
    };

    return ApiService.post(`api/ai/chat/message`, payload)
      .then((res) => {
        commit(SET_STATUS, "online");
        return res;
      })
      .catch((err) => {
        const code = err?.response?.status;
        if (!code || [401, 403, 404, 500].includes(code)) {
          commit(SET_STATUS, "offline");
        }
        commit(SET_ERROR, err);
        return Promise.reject(err);
      })
      .finally(() => commit(SET_LOADING, false));
  },

  [AI_DOWNLOAD_REPORT](_ctx, { url, headers }) {
    const headersWithLang = addLanguageHeader(headers);
    return publicHttpClient.get(url, {
      headers: headersWithLang,
      responseType: "blob",
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
